import { LoadDecorator } from 'decorators'
import { omit, concat, map, set, merge, findIndex, filter } from 'lodash'
import { http, queryString } from 'utils'

// Load mutation imports.
import {
  SET_LOADS, SET_LOADS_PAGINATION, ADD_LOADS, ADD_LOAD, SET_LOAD, RESET_LOAD,
  SET_LOAD_ATTRIBUTE, REMOVE_LOAD, ADD_LOAD_CHARGE, REMOVE_LOAD_CHARGE, SET_TOKEN,
  RESET_LOADS
} from 'vx/mutations'

export const initialLoadState = (load = {}) => {
  return merge({
    id: null,
    carrier_id: null,
    carrier: null,
    commodity: {},
    job_id: null,
    truck_id: null,
    truck: {},
    truck_configuration_id: null,
    driver_id: null,
    driver: {},
    reference_number: null,
    loading_time: null,
    loading_date: null,
    delivery_time: null,
    delivery_date: null,
    rate: null,
    size: null,
    status: 'planned',
    request_sent: false,
    order: null,
    charges: [],
    weighbridge: null,
    notes: null
  }, load)
}

// Load state.
const state = {
  loads: [],
  load: initialLoadState(),
  token: null
}

// Load state getters.
const getters = {
  /**
   * The load payload excludes the relationship objects.
   */
  loadPayload: state => omit(state.load, ['location', 'driver', 'carrier', 'truck']),

  /**
   * Loads decorated via the LoadDecorator.
   */
  loadsDecorated: state => map(state.loads, load => (new LoadDecorator(load))),

  /**
   * Decorated load.
   */
  loadDecorated: state => (new LoadDecorator(state.load)),

  /**
   * Determine if we have a load.
   */
  haveLoad: state => state.load.id !== null && state.load.id !== undefined,

  /**
   * Determine if we have any loads.
   */
  haveLoads: state => state.loads.length > 0,

  /**
   * Determine if we have a load that has a weighbridge.
   */
  haveLoadWithWeighbridge: state => state.load.id !== null && state.load.id !== undefined && state.load.weighbridge !== null,

  /**
   * Determine if we have no loads.
   */
  haveNoLoads: state => state.loads.length === 0
}

// Load actions.
const actions = {
  /**
   * Get all loads.
   */
  getLoads ({ commit }, payload) {
    commit(RESET_LOADS)

    return http.get(`loads?${queryString(payload)}`).then(response => {
      commit(SET_LOADS, response.data.data)
      commit(SET_LOADS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get the loads for the next page.
   */
  getLoadsForNextPage ({ commit, state }, payload) {
    if (state.pagination.current_page === state.pagination.last_page || state.pagination.total === 0) {
      return Promise.reject('There are no more loads to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`loads?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_LOADS, response.data.data)
      commit(SET_LOADS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Store a new load.
   */
  storeLoad ({ commit, state }, { job, payload }) {
    return http.post(`jobs/${job}/loads`, omit(payload, ['location', 'driver', 'carrier', 'truck', 'job']))
  },

  /**
   * Schedule a load.
   */
  scheduleLoad ({ commit }, id) {
    return http.post(`loads/${id}/schedule`)
  },

  /**
   * Withdraw user from the load.
   */
  withdrawFromLoad ({ commit }, id) {
    return http.post(`loads/${id}/withdraw`)
  },

  /**
   * Send a load request to the nominated carrier.
   */
  requestLoad ({ commit }, id) {
    return http.post(`loads/${id}/request`)
  },

  /**
   * Update an existing load's data.
   */
  updateLoad ({ commit }, { id, payload }) {
    return http.put(`loads/${id}`, omit(payload, ['location', 'driver', 'carrier', 'truck'])).then(response => {
      commit(SET_LOAD, response.data)

      return response
    })
  },

  /**
   * Retrieve a load by ID.
   */
  getLoad ({ commit }, id) {
    return http.get(`loads/${id}`).then(response => {
      commit(SET_LOAD, response.data)

      return response
    })
  },

  /**
   * Delete a load.
   */
  deleteLoad ({ commit }, id) {
    return http.delete(`loads/${id}`).then(response => {
      commit(SET_LOAD, initialLoadState())

      return response
    })
  },

  /**
   * Accept a load as a carrier.
   */
  acceptLoad ({ commit }, id) {
    return http.post(`loads/${id}/accept`).then(response => {
      commit(REMOVE_LOAD, response.data)

      return response
    })
  },

 /**
  * Decline a load as a carrier.
  */
  declineLoad ({ commit }, id) {
    return http.post(`loads/${id}/decline`).then(response => {
      commit(REMOVE_LOAD, response.data)

      return response
    })
  },

  /**
   * Cancel a load as an admin or coordinator.
   */
  cancelLoad ({ commit }, id) {
    return http.post(`loads/${id}/cancel`).then(response => {
      commit(SET_LOAD, response.data)

      return response
    })
  },

  /**
   * Store a load charge.
   */
  storeLoadCharge ({ commit }, {loadId, payload}) {
    return http.post(`charges/${loadId}`, payload).then(response => {
      commit(ADD_LOAD_CHARGE, response.data)

      return response
    })
  },

  /**
   * Delete a load charge.
   */
  deleteLoadCharge ({ commit }, chargeId) {
    return http.delete(`charges/${chargeId}`).then(response => {
      commit(REMOVE_LOAD_CHARGE, chargeId)

      return response
    })
  },

  /**
   * Send a driver a message with load details via email.
   */
  emailDriver ({ commit }, { id, payload }) {
    return http.post(`loads/${id}/email-driver`, payload).then(response => {
      return response
    })
  },

  /**
   * Send a driver a message via SMS.
   */
  smsDriver ({ commit }, { id, payload }) {
    return http.post(`loads/${id}/sms-driver`, payload).then(response => {
      return response
    })
  },

  /**
   * Retrieve a load by a reference_number inputted by a user.
   */
  findLoadByReference ({ commit }, payload) {
    return http.get(`loads/reference?${queryString(payload)}`).then(response => {
      return response
    })
  }
}

// Load state mutations.
const mutations = {
  /**
  * Set the loads.
  */
  [SET_LOADS] (state, loads) {
    state.loads = loads
  },

  /**
   * Add more loads to the loads state.
   */
  [ADD_LOADS] (state, loads) {
    state.loads = concat(state.loads, loads)
  },

  /**
   * Set the load pagination.
   */
  [SET_LOADS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Set the state load.
   */
  [SET_LOAD] (state, load) {
    state.load = load
  },

  /**
   * Reset the load.
   */
  [RESET_LOAD] (state) {
    state.load = initialLoadState()
  },

  /**
   * Set loads attribute.
   */
  [SET_LOAD_ATTRIBUTE] (state, { attribute, value }) {
    set(state.load, attribute, value)
  },

  /**
   * Remove a load.
   */
  [REMOVE_LOAD] (state, load) {
    let existingIndex = findIndex(state.loads, existing => existing.id === load.id)

    if (existingIndex > -1) {
      state.loads.splice(existingIndex, 1)
    }
  },

  /**
   * Add a load or replace an existing.
   */
  [ADD_LOAD] (state, load) {
    let existingLoad = findIndex(state.loads, existing => existing.id === load.id)

    if (existingLoad === -1) {
      state.loads.push(load)
    } else {
      state.loads.splice(existingLoad, 1, load)
    }
  },

  /**
   * Add charge to the trucks charges array
   */
  [ADD_LOAD_CHARGE] (state, charge) {
    state.load.charges.push(charge)
  },

  /**
   * Remove a charge from the trucks charges array
   */
  [REMOVE_LOAD_CHARGE] (state, chargeId) {
    state.load.charges = filter(state.load.charges, existing => existing.id !== chargeId)
  },

  /**
   * Set load authorization token.
   */
  [SET_TOKEN] (state, token) {
    state.token = token
  },

  /**
   * Reset the loads.
   */
  [RESET_LOADS] (state) {
    state.loads = []
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}



// WEBPACK FOOTER //
// ./src/vuex/modules/load.js